/*
 * iOS Safari lets the page keep scrolling behind an open drawer/modal even though the theme
 * sets `overflow: hidden` on <html> (its `.lock` class) while one is open — that CSS alone
 * doesn't stop touch-drag scrolling on iOS, so the background page (and with it, the drawer
 * that rides along) visibly shifts up while the user drags on the page behind it.
 *
 * Getting a reliable "is anything open right now" signal took a few tries:
 *  - The theme's own `.lock` class on <html> is applied unreliably (confirmed by testing: some
 *    opens never got it), so keying off it silently misses cases.
 *  - Every drawer/modal/popover/search dialog (:where(.modal, .drawer, .popover, .header-search)
 *    in theme.css) is a custom element with its OWN shadow root, and shows/hides itself inside
 *    that shadow root. A MutationObserver on the light DOM never sees that, so it caught opens
 *    but not closes.
 *  - The `[part="overlay"]` element's opacity turned out not to reliably track open/closed either
 *    (observed opacity 0 while a drawer was genuinely open on screen).
 *
 * The reliable ground truth is whether the dialog's `[part="content"]` panel is actually
 * occupying screen space: rendered at all (getClientRects().length > 0) AND its bounding box
 * overlaps the viewport (closed drawers are still rendered, just translated off-screen). This is
 * polled rather than observed, since the relevant state lives inside several separate shadow roots.
 *
 * One more wrinkle, found after shipping the first version: changing a line item's quantity in
 * the cart drawer re-renders the drawer's contents via AJAX (Shopify swaps in the updated section
 * HTML). For a tick or two during that swap, the content panel can appear to have zero rects —
 * not because the drawer closed, but because its DOM is mid-replacement. Releasing the scroll
 * lock right then unpinned <body> and fired a real scroll event, which the drawer's own
 * click/scroll-outside-closes logic picked up as "the user scrolled away" and closed itself. So
 * "open" is trusted immediately (never wrongly delays pinning), but "closed" only takes effect
 * after it's been seen on several consecutive ticks in a row, filtering out that transient blip
 * without meaningfully delaying the real close.
 *
 * ------------------------------------------------------------------------------------------
 * PERFORMANCE (added later — this is why the polling now sleeps)
 *
 * The first version ran setInterval(tick, 120) for the entire life of the page, and every tick
 * did a querySelectorAll plus getClientRects()/getBoundingClientRect() on each host. Those calls
 * force a synchronous layout, so the page was paying a forced reflow roughly eight times a second
 * forever, including while completely idle. On phone-class CPUs that keeps the main thread busy
 * and makes every tap feel sluggish — which is exactly the symptom being chased.
 *
 * The detection logic below is UNCHANGED (same shadow-root walk, same overlap test, same
 * CLOSE_CONFIRM_TICKS = 4 guard against the AJAX re-render blip). What changed is when it runs:
 *
 *  - The timer sleeps after IDLE_TICKS_BEFORE_SLEEP ticks with nothing open and no lock held.
 *  - Any pointerdown / click / keydown wakes it back up, all registered as passive+capture so
 *    they never delay or interfere with the events themselves.
 *  - Nothing can open a dialog without one of those events happening first, so no open can be
 *    missed; the worst case is the same one tick of latency the original had.
 *  - The host list is cached on wake instead of being re-queried every tick, and it is
 *    invalidated on wake so section re-renders that swap hosts are picked up.
 *  - The timer also stops while the tab is hidden.
 *
 * PERFORMANCE, ROUND 2 (2026-09-02): the fix above stops the poll once the page goes idle, but
 * `wake()` was still called unconditionally at parse time, which started the setInterval(tick,
 * 120) loop immediately — right as images/fonts/sections are still loading and the page is
 * already busy with its own layout work. Every tick's getClientRects()/getBoundingClientRect()
 * forces a synchronous layout, so during that initial load window each 120ms tick was forcing a
 * reflow on an already-thrashing page. Confirmed via Lighthouse's "Forced reflow" audit: this
 * file alone accounted for ~1.4s of forced layout during page load, more than any single
 * third-party script on the page.
 *
 * Fix: still do one synchronous anyDialogOpen() check immediately (covers a dialog that's
 * already open at parse time, e.g. the popup re-opening itself right after its own form post),
 * but only START THE RECURRING TIMER right away if that check finds something open. Otherwise,
 * the timer doesn't start until the page's `load` event (or immediately if the script happens to
 * run after load already fired) — by then the page's own layout churn has settled, so the same
 * polling costs far less. User-triggered opens are unaffected either way, since the
 * pointerdown/click/keydown listeners below are registered immediately and wake() the timer the
 * instant one fires, load event or not.
 * ------------------------------------------------------------------------------------------
 */
(function () {
  var body = document.body;
  var savedScrollY = 0;
  var locked = false;
  var consecutiveClosedTicks = 0;
  var idleTicks = 0;
  var timer = null;
  var cachedHosts = null;

  var CLOSE_CONFIRM_TICKS = 4; // ~480ms at 120ms/tick — comfortably longer than an AJAX cart re-render
  var IDLE_TICKS_BEFORE_SLEEP = 25; // ~3s of nothing open before the timer stops

  function getHosts() {
    if (cachedHosts === null) {
      cachedHosts = document.querySelectorAll('.modal, .drawer, .popover, .header-search');
    }

    return cachedHosts;
  }

  function isVisibleOnScreen(host) {
    var root = host.shadowRoot;
    if (!root) return false;

    var panel = root.querySelector('[part~="content"]') || root.querySelector('[part~="overlay"]');
    if (!panel) return false;

    if (panel.getClientRects().length === 0) return false;

    var r = panel.getBoundingClientRect();
    return r.right > 0 && r.left < window.innerWidth && r.bottom > 0 && r.top < window.innerHeight;
  }

  function anyDialogOpen() {
    var hosts = getHosts();

    for (var i = 0; i < hosts.length; i++) {
      if (isVisibleOnScreen(hosts[i])) {
        return true;
      }
    }

    return false;
  }

  function applyLock() {
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    body.style.position = 'fixed';
    body.style.top = -savedScrollY + 'px';
    body.style.left = '0';
    body.style.right = '0';
    locked = true;
  }

  function releaseLock() {
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    window.scrollTo(0, savedScrollY);
    locked = false;
  }

  function sleep() {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }

    idleTicks = 0;
  }

  function wake() {
    idleTicks = 0;
    cachedHosts = null;

    if (timer === null) {
      timer = setInterval(tick, 120);
      tick();
    }
  }

  function tick() {
    if (anyDialogOpen()) {
      consecutiveClosedTicks = 0;
      idleTicks = 0;

      if (!locked) {
        applyLock();
      }

      return;
    }

    if (locked) {
      idleTicks = 0;
      consecutiveClosedTicks++;

      if (consecutiveClosedTicks >= CLOSE_CONFIRM_TICKS) {
        releaseLock();
        consecutiveClosedTicks = 0;
      }

      return;
    }

    idleTicks++;

    if (idleTicks >= IDLE_TICKS_BEFORE_SLEEP) {
      sleep();
    }
  }

  ['pointerdown', 'click', 'keydown'].forEach(function (type) {
    document.addEventListener(type, wake, { capture: true, passive: true });
  });

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      sleep();
    } else {
      wake();
    }
  });

  if (anyDialogOpen() || document.readyState === 'complete') {
    wake();
  } else {
    window.addEventListener('load', wake, { once: true });
  }
})();